import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { uploadAuditPDF, getAuditReport, UploadAuditPDFParams, getAiServiceAgreement, signAiServiceSubscription, previewAiServiceAgreement,  getRecentAiAuditDetailsById, getPatientAiAuditData, searchAuditDataList, searchInFlightAuditsForUser, type SearchAiAuditParams, getAllAuditDetailsById, getAiAuditDetailById, uploadFacesheetForAIExtraction, UploadFacesheetParams } from "../../../service/AiAuditService";
import { LoadingType, GeneralV3Response } from "../../../types/CommonTypes";
import { useAppSelector } from "../redux-hooks";

// CS-1773 - Run Medical Note Audit AI Tool in Background - Start
export type AuditJobStatus = "uploading" | "processing" | "completed" | "failed";

export interface AuditJobFileMetadata {
    fileName: string;
    fileType: string;
    docType: string;
    patientIdAtStart: string | number | null;
    patientNameAtStart?: string;
}

export interface AuditJob {
    documentId: string;
    auditId: string | null;
    status: AuditJobStatus;
    data: any | null;
    error: string | null;
    fileMetadata?: AuditJobFileMetadata;
}

export interface AuditCompletionEvent {
    documentId: string;
    auditId: string;
    patientId: string | number | null;
    patientName: string;
    fileName: string;
    auditData: any;
    completedAt: number;
}
// CS-1773 - Run Medical Note Audit AI Tool in Background - End

// Export search params type
export type SearchAiAuditInfo = SearchAiAuditParams;

// AI Audit List Response type
export type AiAuditInfo = {
    auditId: string;
    aiAuditDataId?: string;
    patientId?: number | null;
    patientName: string;
    practiceName: string;
    fileName: string;
    createdAt: string;
    createdByUserName?: string;
    currentCompliancePercentage: number;
    // CS - 1790
    status?: string;
    patientStatus?: string;
};

export type AiAuditListResponse = {
    searchAuditData: {
        content: AiAuditInfo[];
        totalPages: number;
        totalElements: number;
        currentPage: number;
    };
    dowloadAuditData?: any;

};

// State interface
interface AiAuditState {
    auditResults: any | null;
    serviceAgreementStatus: any | null;
    agreementPdfContent: any | null;
    auditId: string | null;
    isLoading: boolean;
    error: string | null;
    uploadStatus: LoadingType;
    fetchStatus: LoadingType;
    aiAuditList: AiAuditListResponse | null;
    fetchAiAuditListStatus: LoadingType;
    allAuditDetails: any | null;
    fetchAllAuditDetailsStatus: LoadingType;
    // CS-1758 - Facesheet Extraction state
    facesheetData: any | null;
    facesheetUploadStatus: LoadingType;
    // CS-1773
    auditJobs: Record<string, AuditJob>;
    auditCompletionEvent: AuditCompletionEvent | null;
}

// Initial state
const initialState: AiAuditState = {
    auditResults: null,
    auditId: null,
    isLoading: false,
    error: null,
    uploadStatus: "idle",
    serviceAgreementStatus: null,
    fetchStatus: "idle",
    agreementPdfContent: null,
    aiAuditList: null,
    fetchAiAuditListStatus: "idle",
    allAuditDetails: null,
    fetchAllAuditDetailsStatus: "idle",
    // CS-1758 - Facesheet Extraction
    facesheetData: null,
    facesheetUploadStatus: "idle",
    // CS-1773
    auditJobs: {},
    auditCompletionEvent: null,
};

// Async thunk for uploading PDF
export const uploadPDFForAudit = createAsyncThunk(
    "aiAudit/uploadPDF",
    async (params: UploadAuditPDFParams, { rejectWithValue }) => {
        try {
            const response = await uploadAuditPDF(params);
            // return response.data;
            return response?.data; // CS-1984
        } catch (error: any) {
            return rejectWithValue(error?.data?.message||"Something went wrong while uploading the PDF.");
        }
    }
);

// Async thunk for fetching audit report
export const fetchAuditReport = createAsyncThunk(
    "aiAudit/fetchReport",
    async (auditId: string, { rejectWithValue }) => {
        try {
            const response = await getAuditReport(auditId);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch audit report");
        }
    }
);

//CS-1594 - Fetch the actual status of AI Service Agreement
export const fetchAiServiceAgreementStatus = createAsyncThunk("aiAudit/fetchServiceAgreementStatus",
    async (serviceName: string, { rejectWithValue }) => {
        try {
            const response = await getAiServiceAgreement(serviceName);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch AI Service Agreement status");
        }
    }
);

export const signAiServiceAgreement = createAsyncThunk("aiAudit/signAiServiceAgreement",
    async (subscriptionData: any, { rejectWithValue }) => {
        try {
            const response = await signAiServiceSubscription(subscriptionData);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to sign AI Service Agreement");
        }
    }
);

export const previewAiServiceAgreementData = createAsyncThunk("aiAudit/previewAiServiceAgreement",
    async (serviceData: any, { rejectWithValue }) => {
        try {
            const response = await previewAiServiceAgreement(serviceData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to preview AI Service Agreement");
        }
    }
);

export const getPatientAiAuditDataByPatientId = createAsyncThunk("aiAudit/getPatientAiAuditDataByPatientId",
    async (patientId: any, { rejectWithValue }) => {
        try {
            const response = await getPatientAiAuditData(patientId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to get AI audit data for patient");
        }
    }
);

export const getRecentAiAuditDetailsByIdData = createAsyncThunk("aiAudit/getRecentAiAuditDetailsByIdData",
    async (auditId: string, { rejectWithValue }) => {
        try {
            const response = await getRecentAiAuditDetailsById(auditId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to get AI audit details");
        }
    }
);

// Async thunk for searching/fetching AI audit list
export const fetchAiAuditList = createAsyncThunk(
    "aiAudit/fetchAiAuditList",
    async (data: SearchAiAuditInfo, { rejectWithValue }) => {
        try {
            const response = await searchAuditDataList(data);
            const v3Response = response.data;
            return v3Response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch AI audit list");
        }
    }
);

// // CS-1773 - Run Medical Note Audit AI Tool in Background
export const hydrateInFlightAudits = createAsyncThunk(
    "aiAudit/hydrateInFlightAudits",
    async (userId: number, { rejectWithValue }) => {
        try {
            const response = await searchInFlightAuditsForUser(userId);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message ||
                    "Failed to hydrate in-flight audits",
            );
        }
    },
);

export const getAllAuditDetailsByIdData = createAsyncThunk("aiAudit/getAllAuditDetailsByIdData",
    async (auditId: string, { rejectWithValue }) => {
        try {
            const response = await getAllAuditDetailsById(auditId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to get all AI audit details");
        }
    });

    export const getAiAuditDetailByIdData = createAsyncThunk("aiAudit/getAiAuditDetailByIdData",
    async (aiAuditDetailId: string, { rejectWithValue }) => {
        try {
            const response = await getAiAuditDetailById(aiAuditDetailId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to get AI audit detail");
        }
    });

// CS-1758 - Facesheet Extraction - Upload and extract facesheet data
export const uploadFacesheetForExtraction = createAsyncThunk(
    "aiAudit/uploadFacesheet",
    async (params: UploadFacesheetParams, { rejectWithValue }) => {
        try {
            const response = await uploadFacesheetForAIExtraction(params);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error?.data?.message || "Something went wrong while extracting facesheet data.");
        }
    }
);

// Create the slice
const aiAuditSlice = createSlice({
    name: "aiAudit",
    initialState,
    reducers: {
        // Clear audit results
        clearAuditResults: (state) => {
            state.auditResults = null;
            state.auditId = null;
            state.error = null;
            state.uploadStatus = "idle";
            state.fetchStatus = "idle";
        },
        
        // Clear error
        clearError: (state) => {
            state.error = null;
        },
        
        // Set loading state manually (if needed)
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        
        // Reset entire state
        resetAuditState: () => {
            return initialState;
        },
        //reset service agreement status
        resetServiceAgreementStatus: (state) => {
            state.serviceAgreementStatus = null;
        },
        // Reset AI audit list
        resetAiAuditList: (state) => {
            state.aiAuditList = null;
            state.fetchAiAuditListStatus = "idle";
        },
        // Reset all audit details
        resetAllAuditDetails: (state) => {
            state.allAuditDetails = null;
            state.fetchAllAuditDetailsStatus = "idle";
        },
        // CS-1758 - Reset facesheet data
        resetFacesheetData: (state) => {
            state.facesheetData = null;
            state.facesheetUploadStatus = "idle";
        },
       // CS-1773 - Run Medical Note Audit AI Tool in Background
        setAuditJob: (
            state,
            action: PayloadAction<{ documentId: string; job: Partial<AuditJob> }>,
        ) => {
            const { documentId, job } = action.payload;
            const existing = state.auditJobs[documentId] || {
                documentId,
                auditId: null,
                status: "uploading" as AuditJobStatus,
                data: null,
                error: null,
            };
            state.auditJobs[documentId] = { ...existing, ...job, documentId };
        },
        clearAuditJob: (state, action: PayloadAction<string>) => {
            delete state.auditJobs[action.payload];
        },
        // Completion event reducers — drives the global completion popup.
        setAuditCompletionEvent: (
            state,
            action: PayloadAction<AuditCompletionEvent>,
        ) => {
            state.auditCompletionEvent = action.payload;
        },
        clearAuditCompletionEvent: (state) => {
            state.auditCompletionEvent = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Upload PDF
            .addCase(uploadPDFForAudit.pending, (state) => {
                state.uploadStatus = "loading";
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadPDFForAudit.fulfilled, (state, action) => {
                state.uploadStatus = "success";
                // state.auditId = action.payload.audit_id || action.payload.auditId;
                state.auditId = (action.payload?.audit_id || action.payload?.auditId) ?? null; // CS-1984
                state.isLoading = false;
            })
            .addCase(uploadPDFForAudit.rejected, (state, action) => {
                state.uploadStatus = "error";
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Audit Report
            .addCase(fetchAuditReport.pending, (state) => {
                state.fetchStatus = "loading";
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAuditReport.fulfilled, (state, action) => {
                state.fetchStatus = "success";
                state.auditResults = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchAuditReport.rejected, (state, action) => {
                state.fetchStatus = "error";
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Audit Report
            .addCase(fetchAiServiceAgreementStatus.pending, (state) => {
                state.fetchStatus = "loading";
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAiServiceAgreementStatus.fulfilled, (state, action) => {
                state.fetchStatus = "success";
                state.serviceAgreementStatus = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchAiServiceAgreementStatus.rejected, (state, action) => {
                state.fetchStatus = "error";
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Preview AI Service Agreement
            .addCase(previewAiServiceAgreementData.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(previewAiServiceAgreementData.fulfilled, (state, action) => {
                state.isLoading = false;
                // Store the PDF content in the state
                state.agreementPdfContent = action.payload;
            })
            .addCase(previewAiServiceAgreementData.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Sign AI Service Agreement
            .addCase(signAiServiceAgreement.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(signAiServiceAgreement.fulfilled, (state, action) => {
                state.isLoading = false;
                state.serviceAgreementStatus = action.payload;
            })
            .addCase(signAiServiceAgreement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch AI Audit List
            .addCase(fetchAiAuditList.pending, (state) => {
                state.fetchAiAuditListStatus = "loading";
            })
            .addCase(fetchAiAuditList.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
                state.fetchAiAuditListStatus = "success";
                // state.aiAuditList = action.payload.data;
                state.aiAuditList = action.payload?.data ?? null; // CS-1984
            })
            .addCase(fetchAiAuditList.rejected, (state, action) => {
                state.fetchAiAuditListStatus = "error";
                state.aiAuditList = null;
                state.error = action.payload as string;
            })
            // Fetch All AI Audit Details by ID
            .addCase(getAllAuditDetailsByIdData.pending, (state) => {
                state.fetchAllAuditDetailsStatus = "loading";
            })
            .addCase(getAllAuditDetailsByIdData.fulfilled, (state, action) => {
                state.fetchAllAuditDetailsStatus = "success";
                // state.allAuditDetails = action.payload.data;
                state.allAuditDetails = action.payload?.data ?? null; // CS-1984
            })
            .addCase(getAllAuditDetailsByIdData.rejected, (state, action) => {
                state.fetchAllAuditDetailsStatus = "error";
                state.allAuditDetails = null;
                state.error = action.payload as string;
            })
            // CS-1758 - Facesheet Extraction
            .addCase(uploadFacesheetForExtraction.pending, (state) => {
                state.facesheetUploadStatus = "loading";
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadFacesheetForExtraction.fulfilled, (state, action) => {
                state.facesheetUploadStatus = "success";
                state.facesheetData = action.payload;
                state.isLoading = false;
            })
            .addCase(uploadFacesheetForExtraction.rejected, (state, action) => {
                state.facesheetUploadStatus = "error";
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

// Export actions
export const { 
    clearAuditResults, 
    clearError, 
    setLoading, 
    resetAuditState,
    resetServiceAgreementStatus,
    resetAiAuditList,
    resetAllAuditDetails,
    resetFacesheetData,
    setAuditJob,
    clearAuditJob,
    setAuditCompletionEvent,
    clearAuditCompletionEvent,
} = aiAuditSlice.actions;

export const useAiAuditReducer = () => useAppSelector((state) => state.aiAuditSlice);

// Export reducer
export default aiAuditSlice.reducer;
